import React from 'react'
import { Horizontal, Texto, MaterialIcon, GraviButton } from '@gravitate-js/excalibrr'
import { Button, Dropdown, Menu, Tag } from 'antd'

interface MatchDetailToolbarProps {
  isManageMode: boolean
  isMatchApproved: boolean
  hasUnsavedChanges: boolean
  hasError: boolean
  error: Error | string | null
  isSaving: boolean
  selectedIds: string[]
  onSave: () => Promise<void>
  onCancel: () => void
  onSortTaxes: (sortField: string) => Promise<void>
  onRecalculateTaxes?: () => Promise<void>
  isRecalculating?: boolean
  canRecalculateTaxes?: boolean
  selectedSort?: string
}

export function MatchDetailToolbar({
  isManageMode,
  isMatchApproved,
  hasUnsavedChanges,
  hasError,
  error,
  isSaving,
  selectedIds,
  onSave,
  onCancel,
  onSortTaxes,
  onRecalculateTaxes,
  isRecalculating = false,
  canRecalculateTaxes = true,
  selectedSort,
}: MatchDetailToolbarProps) {
  const getSortDisplayName = (key: string) => {
    const sortOptions = {
      tax_description: 'Tax Description',
      per_unit: 'Per Unit',
      product_name: 'Product',
      total: 'Total',
    }
    return sortOptions[key] || 'Sort Items By'
  }
  return (
    <Horizontal
      bg='elevated'
      className='px-3 py-2'
      flex='0'
      style={{
        overflow: 'visible',

        width: '100%',
      }}
      justifyContent='spaceBetween'
      border='bottom'
      verticalCenter
    >
      {isManageMode ? (
        <>
          <Horizontal gap={16} verticalCenter>
            <Texto category='h4' weight='bold' style={{ whiteSpace: 'nowrap' }}>
              MANAGE MATCH
            </Texto>
            {hasUnsavedChanges && <Tag color='orange'>Unsaved Changes</Tag>}
            {hasError && (
              <Texto appearance='error' category='p2'>
                {typeof error === 'string' ? error : 'Failed to load match options'}
              </Texto>
            )}
          </Horizontal>
          <Horizontal gap={12} verticalCenter>
            <Button onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <GraviButton
              theme2
              disabled={selectedIds.length === 0 || isSaving}
              loading={isSaving}
              icon={<MaterialIcon icon='save' />}
              buttonText='Save Matches'
              onClick={onSave}
            />
          </Horizontal>
        </>
      ) : (
        <>
          <Texto category='h4' weight='bold' style={{ flex: 1, whiteSpace: 'nowrap' }}>
            TAXES
          </Texto>
          <Horizontal style={{ flex: 0, overflow: 'visible' }} gap={8} verticalCenter>
            <Dropdown
              overlay={
                <Menu
                  onClick={async ({ key }) => {
                    await onSortTaxes(key as string)
                  }}
                >
                  <Menu.Item key='tax_description'>Tax Description</Menu.Item>
                  <Menu.Item key='per_unit'>Per Unit</Menu.Item>
                  <Menu.Item key='product_name'>Product</Menu.Item>
                  <Menu.Item key='total'>Total</Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement='bottomRight'
            >
              <Button
                size='small'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  background: '#fff',
                  color: 'rgba(0, 0, 0, 0.88)',
                  minWidth: '140px',
                  justifyContent: 'space-between',
                  padding: '4px 11px',
                }}
              >
                <span>{getSortDisplayName(selectedSort || '')}</span>
                <MaterialIcon icon='arrow_drop_down' size={16} />
              </Button>
            </Dropdown>
            <GraviButton
              size='small'
              disabled={isMatchApproved || isRecalculating || !canRecalculateTaxes}
              loading={isRecalculating}
              icon={<MaterialIcon icon='refresh' />}
              buttonText='Recalculate Taxes'
              onClick={onRecalculateTaxes}
            />
          </Horizontal>
        </>
      )}
    </Horizontal>
  )
}
